import { useCallback, useEffect, useRef, useState } from 'react';
import SimpleModal from 'components/SimpleModal';
import DatePicker from 'components/DatePicker';
import { Box } from '@mui/material';
import { CsmSelector } from 'CsmModule/components/CsmSelector';
import { useCsmSelector } from 'CsmModule/hooks/useCsmSelector';
import { useCsmAssignments } from 'CsmModule/hooks/useCsmAssignments';
import useTeams from 'hooks/useTeams';
import { formatDate } from 'utils';

export type EditCsmModalProps = { teamId: number; onClose: VoidFunction };

const EditCsmModal: React.FC<EditCsmModalProps> = ({ teamId, onClose }) => {
  const { teamHash } = useTeams({ shouldAutoLoad: false });
  const team = teamHash[teamId];
  const initialStart = team?.created_at || '';
  const { createCsmAssignment, updateCsmAssignment, csmAssignmentByTeam, fetchCsmAssignments } =
    useCsmAssignments({ isOff: true });
  const { onCsmSelectorClick, selectedCsm } = useCsmSelector({
    accountId: csmAssignmentByTeam[teamId]?.account_id ?? null,
  });
  const existing = csmAssignmentByTeam[teamId];
  const [startDate, setStartDate] = useState<string>(
    formatDate(existing?.start_date || initialStart || ''),
  );
  const [endDate, setEndDate] = useState<string>(formatDate(existing?.end_date || ''));
  const [touchedStart, setTouchedStart] = useState(false);
  const [touchedEnd, setTouchedEnd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  const onConfirm = useCallback(() => {
    setSubmitting(true);
    setError('');

    const effectiveCsm =
      selectedCsm ||
      (existing ? { accountId: existing.account_id, name: existing.account_name } : undefined);
    if (!effectiveCsm) {
      setSubmitting(false);
      setError('Please select a CSM');
      return;
    }

    const base = {
      mosaicAccountId: effectiveCsm.accountId,
      mosaicTeamId: teamId,
      csmName: effectiveCsm.name,
      startDate: startDate || formatDate(team?.created_at || ''),
      endDate: endDate || null,
    } as any;

    if (existing) {
      updateCsmAssignment({
        id: existing.id,
        ...base,
        onSuccess: () => {
          fetchCsmAssignments({ forceRefetch: true, mosaicTeamIds: [teamId] });
          setSubmitting(false);
          onClose();
        },
        onFailure: ({ error }: any) => {
          setSubmitting(false);
          setError((error as any)?.message || 'Failed to update CSM assignment');
        },
      });
    } else {
      createCsmAssignment({
        ...base,
        onSuccess: () => {
          fetchCsmAssignments({ forceRefetch: true, mosaicTeamIds: [teamId] });
          setSubmitting(false);
          onClose();
        },
        onFailure: ({ error }: any) => {
          setSubmitting(false);
          setError((error as any)?.message || 'Failed to create CSM assignment');
        },
      });
    }
  }, [
    selectedCsm,
    existing,
    startDate,
    endDate,
    team?.created_at,
    teamId,
    updateCsmAssignment,
    fetchCsmAssignments,
    onClose,
    createCsmAssignment,
  ]);

  const lastFetchedTeamIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (lastFetchedTeamIdRef.current === teamId) return;
    fetchCsmAssignments({ forceRefetch: true, mosaicTeamIds: [teamId] });
    lastFetchedTeamIdRef.current = teamId;
  }, [teamId, fetchCsmAssignments]);

  // Populate start/end dates once data is available, but don't clobber user edits

  useEffect(() => {
    if (!touchedStart) {
      const nextStart = formatDate(existing?.start_date || initialStart || '');

      setStartDate(nextStart || '');
    }
    if (!touchedEnd) {
      const nextEnd = formatDate(existing?.end_date || '');

      setEndDate(nextEnd || '');
    }
    // We intentionally depend on the specific fields to avoid unnecessary resets
    // DO NOT add startDate/endDate to deps - that creates circular dependency
  }, [existing?.start_date, existing?.end_date, initialStart, touchedStart, touchedEnd]);

  return (
    <SimpleModal
      open
      onClose={onClose}
      onConfirm={onConfirm}
      disableConfirmButton={submitting}
      headerText={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>Edit CSM</span>
          <span style={{ cursor: 'pointer' }} onClick={onClose}>
            ×
          </span>
        </Box>
      }
      confirmButtonText="Update"
      Body={
        <Box>
          <Box
            sx={{
              mb: 2,
            }}
          >
            <CsmSelector
              accountId={selectedCsm?.accountId ?? existing?.account_id ?? null}
              onClick={(e) => onCsmSelectorClick({ target: e.currentTarget })}
              tooltip="Set CSM"
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}
          >
            <DatePicker
              label="CSM Start Date"
              helperText="CSM's start date"
              value={startDate || ''}
              onChange={(newDate) => {
                setStartDate(newDate ?? '');
                setTouchedStart(true);
              }}
            />
            <DatePicker
              label="CSM End Date"
              helperText="CSM's end date"
              value={endDate || ''}
              onChange={(newDate) => {
                setEndDate(newDate ?? '');
                setTouchedEnd(true);
              }}
            />
          </Box>
          {error ? (
            <Box
              sx={{
                mt: 2,
                color: 'red',
              }}
            >
              {error}
            </Box>
          ) : null}
        </Box>
      }
    />
  );
};

export default EditCsmModal;
export { EditCsmModal };
